import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import InitialsAvatar from "../../../common/components/functional/initialsAvatar/InitialsAvatar";
import { sanitizeForRendering } from "../../../common/helpers/html";
import { ReactComponent as AttachmentIcon } from "../../../common/icons/attachment.svg";
import UserMessageChannelIcons from "../../userMessages/UserMessageChannelIcons";
import {
  getInAppReadReceipt,
  getSentStatusDisplay,
  getUserMessageChannelLabelKey,
  normalizeUserMessageChannels,
} from "../../userMessages/userMessagesHelpers";
import { fetchAttachmentDownloadUrl } from "../../userMessages/userMessagesSlice";
import { UserMessageByIdResponse } from "../../userMessages/userMessagesTypes";

interface HeaderProps {
  senderName: string;
  fromNoReplyAddress: boolean | null;
  hasSenderDetails: boolean;
  includesEmail: boolean;
  isInAppNotice: boolean;
}

export const UserMessageDialogHeader = ({
  senderName,
  fromNoReplyAddress,
  hasSenderDetails,
  includesEmail,
  isInAppNotice,
}: HeaderProps) => {
  const { t } = useTranslation("userMessages");

  return (
    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
      {isInAppNotice ? (
        <Box
          component="img"
          src="/assets/eezy_round_logo.svg"
          alt={senderName}
          sx={{ width: 34, height: 34, borderRadius: "100%", flexShrink: 0 }}
        />
      ) : (
        <InitialsAvatar name={hasSenderDetails ? senderName : ""} />
      )}
      <Box>
        <Typography
          display="block"
          textTransform="uppercase"
          color="text.secondary"
          fontWeight={600}
          fontSize={14}
        >
          {t("sender")}
        </Typography>
        <Typography variant="body1" display="block" lineHeight={1.2}>
          {senderName}
        </Typography>
        {includesEmail && fromNoReplyAddress != null && (
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={14}
            display="block"
          >
            {fromNoReplyAddress
              ? t("sentFromNoReply")
              : t("sentFromPersonalEmail")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface ContentProps {
  message: UserMessageByIdResponse;
  customerName: string;
}

const UserMessageDialogContent = ({
  message,
  customerName,
}: ContentProps) => {
  const { t } = useTranslation(["userMessages", "sendEmail"]);
  const dispatch = useAppDispatch();
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
  const sentStatus = getSentStatusDisplay(message);
  const channels = normalizeUserMessageChannels(message.channels);
  const readReceipt = getInAppReadReceipt(message);

  const handleDownload = async (pathspec: string, index: number) => {
    if (downloadingIndex !== null) return;
    setDownloadingIndex(index);
    const result = await dispatch(
      fetchAttachmentDownloadUrl({ messageId: message.messageId, pathspec }),
    );
    setDownloadingIndex(null);
    if (isFulfilled(result)) {
      window.open(result.payload.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        gap={2}
        mb={2}
        ml="40px"
        alignItems="center"
        lineHeight={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="secondary">{t("customer")}</Typography>
          <Chip
            label={customerName}
            size="small"
            sx={{
              "& .MuiChip-label": { fontSize: 14 },
              marginBottom: 0,
              marginLeft: 0,
            }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <UserMessageChannelIcons channels={channels} />
          <Typography variant="secondary">
            {t(getUserMessageChannelLabelKey(channels))}
          </Typography>
        </Box>
        {sentStatus.kind === "sent" ? (
          <Typography variant="secondary" display="block">
            {t("sentAt")}{" "}
            <Box component="span" color="text.primary">
              {sentStatus.date}
            </Box>
          </Typography>
        ) : (
          <Typography
            variant="secondary"
            display="block"
            color={sentStatus.kind === "failed" ? "error" : undefined}
          >
            {sentStatus.kind === "failed"
              ? t("sendFailed")
              : t("processing")}
          </Typography>
        )}
        {readReceipt?.kind === "read" ? (
          <Typography variant="secondary" display="block">
            {t("readAt")}{" "}
            <Box component="span" color="text.primary">
              {readReceipt.date}
            </Box>
          </Typography>
        ) : readReceipt?.kind === "unread" ? (
          <Typography variant="secondary" display="block">
            {t("unread")}
          </Typography>
        ) : null}
      </Box>
      <Typography
        fontWeight={600}
        fontSize={20}
        variant="header"
        textTransform="uppercase"
      >
        {message.subject}
      </Typography>
      <Box sx={{ marginTop: 2, "& p": { margin: 0 } }}>
        {sanitizeForRendering(message.messageHtml)}
      </Box>
      {message.attachments.length > 0 && (
        <Box mt={2}>
          <Typography
            variant="secondary"
            fontWeight={600}
            display="block"
            mb={0.5}
          >
            {t("attachments")}
          </Typography>
          {message.attachments.map((attachment, index) => (
            <Box
              key={attachment.pathspec}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#F3F3F7",
                borderRadius: "4px",
                padding: "4px 8px",
                mt: 0.5,
              }}
            >
              <AttachmentIcon
                style={{
                  width: 16,
                  height: 16,
                  opacity: 0.6,
                  flexShrink: 0,
                }}
              />
              <Typography variant="secondary" sx={{ flex: 1 }}>
                {attachment.filename ||
                  `${t("sendEmail:attachment")} ${index + 1}`}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleDownload(attachment.pathspec, index)}
                disabled={downloadingIndex !== null}
                title={t("sendEmail:downloadAttachment")}
              >
                {downloadingIndex === index ? (
                  <CircularProgress size={16} />
                ) : (
                  <DownloadIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default UserMessageDialogContent;
